import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Check, AlertCircle, BrainCircuit, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

const GlobalAiSuggestionModal = ({
    isOpen,
    onClose,
    suggestions, // { [key]: [suggestion1, suggestion2, ...] }
    isLoading,
    error,
    onAcceptSuggestions,
    onRegenerate, // New prop for regeneration
    ruleNamesMap = {} // Map of rule IDs to names
}) => {
    const { t } = useTranslation();
    const [selectedSuggestions, setSelectedSuggestions] = useState({}); // { [key]: [suggestionIndex1, suggestionIndex2] }

    // Helper to toggle selection
    const handleToggleSuggestion = (key, suggestionIndex) => {
        setSelectedSuggestions(prev => {
            const currentSelection = prev[key] || [];
            if (currentSelection.includes(suggestionIndex)) {
                return { ...prev, [key]: currentSelection.filter(i => i !== suggestionIndex) };
            } else {
                return { ...prev, [key]: [...currentSelection, suggestionIndex] };
            }
        });
    };

    // Helper to select all for a specific key
    const handleSelectAllForKey = (key) => {
        setSelectedSuggestions(prev => ({
            ...prev,
            [key]: suggestions[key].map((_, index) => index)
        }));
    };

    // Helper to deselect all for a specific key
    const handleDeselectAllForKey = (key) => {
        setSelectedSuggestions(prev => {
            const newState = { ...prev };
            delete newState[key];
            return newState;
        });
    };

    const handleAccept = () => {
        // Transform selected indices back to suggestion objects
        const accepted = {};
        Object.keys(selectedSuggestions).forEach(key => {
            const indices = selectedSuggestions[key];
            if (indices && indices.length > 0) {
                accepted[key] = indices.map(index => suggestions[key][index]);
            }
        });
        onAcceptSuggestions(accepted);
    };

    if (!isOpen) return null;

    const totalSuggestionsCount = suggestions ? Object.values(suggestions).reduce((acc, arr) => acc + arr.length, 0) : 0;
    const totalSelectedCount = Object.values(selectedSuggestions).reduce((acc, arr) => acc + arr.length, 0);

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[120] p-4 print:hidden">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col relative overflow-hidden"
            >
                {/* Header */}
                <div className="bg-indigo-600 p-6 text-white flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <BrainCircuit size={24} />
                            {t('common.aiAssistant', 'Suggestions Assistant ACG')}
                        </h2>
                        <p className="text-indigo-100 text-sm mt-1">
                            {isLoading
                                ? t('securityControls.aiModal.generating', 'Analyse de vos règles et génération de mesures en cours...')
                                : t('securityControls.aiModal.review', 'Examinez et sélectionnez les mesures suggérées pour vos règles.')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Regenerate Button */}
                        {!isLoading && totalSuggestionsCount > 0 && onRegenerate && (
                            <button
                                onClick={onRegenerate}
                                className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors flex items-center gap-2 px-3"
                                title={t('securityControls.buttons.regenerate', 'Actualiser / Autres')}
                            >
                                <RefreshCw size={20} />
                                <span className="text-sm font-medium hidden sm:inline">{t('securityControls.buttons.regenerate', 'Actualiser')}</span>
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                            disabled={isLoading}
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                    {isLoading && totalSuggestionsCount === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 space-y-4">
                            <RefreshCw size={48} className="text-indigo-600 animate-spin" />
                            <p className="text-gray-600 font-medium text-lg">{t('securityControls.aiModal.processing', 'L\'IA travaille pour vous...')}</p>
                            <p className="text-gray-400 text-sm max-w-md text-center">
                                {t('securityControls.aiModal.waitMessage', 'Cela peut prendre quelques instants selon le nombre de règles à analyser.')}
                            </p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-4">
                            <AlertCircle size={24} className="text-red-600 shrink-0 mt-1" />
                            <div>
                                <h3 className="text-red-800 font-semibold mb-1">{t('common.error', 'Erreur')}</h3>
                                <p className="text-red-600">{error}</p>
                            </div>
                        </div>
                    ) : totalSuggestionsCount === 0 ? (
                        <div className="text-center py-12">
                            <BrainCircuit size={64} className="text-indigo-400 mx-auto mb-4 animate-pulse" />
                            <h3 className="text-xl font-medium text-gray-800 mb-2">
                                {t('securityControls.aiModal.generating', 'Génération en cours...')}
                            </h3>
                            <p className="text-gray-500">
                                {t('securityControls.aiModal.waitMessage', 'Cela peut prendre quelques instants selon le nombre de règles à analyser.')}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {Object.entries(suggestions).map(([key, items]) => {
                                // Parse key to get rule ID and dreaded event ID
                                const [ruleId, deId] = key.split('_');
                                const ruleName = ruleNamesMap[ruleId] || `Règle ${ruleId}`;

                                return (
                                    <div key={key} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                            <div>
                                                <span className="text-sm font-semibold text-gray-700">{ruleName}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleSelectAllForKey(key)}
                                                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                                >
                                                    {t('common.selectAll', 'Tout sélectionner')}
                                                </button>
                                                <span className="text-gray-300">|</span>
                                                <button
                                                    onClick={() => handleDeselectAllForKey(key)}
                                                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                                                >
                                                    {t('common.deselectAll', 'Tout désélectionner')}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {items.map((suggestion, index) => {
                                                const isSelected = (selectedSuggestions[key] || []).includes(index);
                                                return (
                                                    <label
                                                        key={index}
                                                        className={`flex items-start p-4 cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/50' : 'hover:bg-gray-50'}`}
                                                    >
                                                        <div className="flex items-center h-5">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleToggleSuggestion(key, index)}
                                                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                                            />
                                                        </div>
                                                        <div className="ml-3 text-sm">
                                                            <span className={`font-medium block ${isSelected ? 'text-indigo-900' : 'text-gray-700'}`}>
                                                                {suggestion.name}
                                                            </span>
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${suggestion.measure_type === 'protection' ? 'bg-green-100 text-green-800' :
                                                                suggestion.measure_type === 'gouvernance et anticipation' ? 'bg-blue-100 text-blue-800' :
                                                                    suggestion.measure_type === 'défense' ? 'bg-orange-100 text-orange-800' :
                                                                        'bg-purple-100 text-purple-800'
                                                                }`}>
                                                                {suggestion.measure_type}
                                                            </span>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-white p-4 border-t border-gray-200 flex justify-between items-center shrink-0">
                    <div className="text-sm text-gray-500">
                        {totalSelectedCount} {t('common.selected', 'sélectionné(s)')}
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors"
                            disabled={isLoading}
                        >
                            {t('common.cancel', 'Annuler')}
                        </button>
                        <button
                            onClick={handleAccept}
                            disabled={isLoading || totalSelectedCount === 0}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            <Check size={16} />
                            {t('common.addSelected', 'Ajouter la sélection')}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>,
        document.body
    );
};

export default GlobalAiSuggestionModal;
